import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { fetchWithAuth } from '../../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EntityModal = ({ entity, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: entity?.name || '', type: entity?.type || 'Investment Company',
    country: entity?.country || '', notes: entity?.notes || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const entityTypes = ['Investment Company', 'Holding Company', 'Property Company', 'Family Office SPV'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const url = entity ? `${BACKEND_URL}/api/entities/${entity.entity_id}` : `${BACKEND_URL}/api/entities`;
      const response = await fetchWithAuth(url, {
        method: entity ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!response.ok) { const error = await response.json(); throw new Error(error.detail || 'Failed to save entity'); }
      toast.success(entity ? 'Entity updated successfully' : 'Entity created successfully');
      onSuccess();
    } catch (error) { toast.error(error.message); }
    finally { setIsSubmitting(false); }
  };

  const fields = [
    { label: 'Entity Name *', type: 'text', key: 'name', placeholder: 'e.g., Smith Holdings Ltd', required: true },
    { label: 'Entity Type *', type: 'select', key: 'type', options: entityTypes, required: true },
    { label: 'Country of Incorporation *', type: 'text', key: 'country', placeholder: 'e.g., United States', required: true },
    { label: 'Notes (Optional)', type: 'textarea', key: 'notes', placeholder: 'Additional notes about this entity' },
  ];

  const renderField = (f) => {
    const baseClass = "w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-emerald-500";
    if (f.type === 'select') {
      return (
        <select required={f.required} value={formData[f.key]} onChange={(e) => setFormData({ ...formData, [f.key]: e.target.value })} className={baseClass}>
          {f.options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    if (f.type === 'textarea') {
      return <textarea value={formData[f.key]} onChange={(e) => setFormData({ ...formData, [f.key]: e.target.value })} rows={3} className={baseClass} placeholder={f.placeholder} />;
    }
    return <input type="text" required={f.required} value={formData[f.key]} onChange={(e) => setFormData({ ...formData, [f.key]: e.target.value })} className={baseClass} placeholder={f.placeholder} />;
  };

  const getSubmitLabel = () => {
    if (isSubmitting) return 'Saving...';
    return entity ? 'Update' : 'Create';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-2xl font-bold text-slate-900 mb-4">{entity ? 'Edit Entity' : 'Add New Entity'}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            {fields.map(f => (
              <div key={f.key}>
                <label className="block text-sm font-medium text-slate-700 mb-1">{f.label}</label>
                {renderField(f)}
              </div>
            ))}
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting} className="flex-1">Cancel</Button>
              <Button type="submit" disabled={isSubmitting} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                {getSubmitLabel()}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EntityModal;
